/**
 * FANTASY QUICK BUILD PAGE
 * 
 * Simple, fast contest creation:
 * 1. Pick style (Players or Teams)
 * 2. Describe your league
 * 3. AI generates 3 creative options
 * 4. Pick one, tweak name/tagline if desired
 * 5. Create!
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowLeft,
  Trophy,
  Loader,
  CheckCircle,
  Send,
  Sparkles,
  Users,
  RefreshCw,
  Edit3,
  ChevronDown,
  ChevronUp,
  Settings,
  Zap,
  DollarSign,
  Clock,
  Target,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { API_BASE_URL } from '../constants'
import { useAuthStore } from '../store/authStore'

// ============================================================================
// TYPES
// ============================================================================

interface ContestOption {
  id: string
  name: string
  tagline: string
  creativeHook: string
  contestType: string
  contestCategory: 'dfs' | 'survivor'
  gameSelection: {
    type: string
    count?: number
    games?: string[]
    description: string
  }
  rosterFormat?: {
    positions: Record<string, any>
    totalPlayers: number
    description: string
    salaryCap: number
    maxPerTeam?: number
  }
  scoring: {
    type: string
    preset?: string
    rules?: Array<{ stat: string; points: number; description: string }>
    formulas?: Array<{ name: string; formula: string; description: string }>
    description: string
    example?: string
  }
  teamScoring?: {
    formula?: string
    rules?: Array<{ stat: string; multiplier: number }>
    winCondition: string
    description: string
  }
  timing: {
    lockTime: string
    lockDescription: string
    rollingLock: boolean
    swapRules?: any
  }
  entry: {
    entryFee: number
    feeDescription: string
    maxEntriesPerUser: number | null
    prizeStructure: string
    prizeDescription: string
  }
  vibe: string
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FantasyQuickBuildPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  
  // Flow state
  const [step, setStep] = useState<'style' | 'describe' | 'options' | 'customize' | 'creating'>('style')
  
  // User inputs
  const [contestStyle, setContestStyle] = useState<'dfs' | 'survivor' | null>(null)
  const [description, setDescription] = useState('')
  
  // AI options
  const [options, setOptions] = useState<ContestOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [spinNumber, setSpinNumber] = useState(0)
  const [canRespin, setCanRespin] = useState(true)
  
  // Selected option & edits
  const [selectedOption, setSelectedOption] = useState<ContestOption | null>(null)
  const [expandedOption, setExpandedOption] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editTagline, setEditTagline] = useState('')
  const [editEntryFee, setEditEntryFee] = useState<number>(0)
  
  // Auto-focus input when reaching describe step
  useEffect(() => {
    if (step === 'describe' && inputRef.current) {
      inputRef.current.focus()
    }
  }, [step])
  
  // ============================================================================
  // GENERATE OPTIONS
  // ============================================================================
  
  const generateOptions = async (isRespin = false) => {
    if (!contestStyle || !description.trim()) {
      toast.error('Please describe your league first!')
      return
    }
    
    setIsLoading(true)
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/fantasy/quick-build/generate`, {
        contestStyle,
        description: description.trim(),
        spinNumber: isRespin ? spinNumber + 1 : 0
      })
      
      if (response.data.success) {
        setOptions(response.data.options)
        setSpinNumber(response.data.spinNumber)
        setCanRespin(response.data.canRespin)
        setStep('options')
        setExpandedOption(null)
      } else {
        toast.error(response.data.error || 'Failed to generate options')
      }
    } catch (error: any) {
      console.error('Generate options error:', error)
      toast.error(error.response?.data?.error || 'Failed to generate options')
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleRespin = () => {
    if (!canRespin) {
      toast.error('No more respins! Try Custom Builder or start over.')
      return
    }
    generateOptions(true)
  }
  
  // ============================================================================
  // SELECT & CUSTOMIZE
  // ============================================================================
  
  const selectOption = (option: ContestOption) => {
    setSelectedOption(option)
    setEditName(option.name)
    setEditTagline(option.tagline)
    setEditEntryFee(option.entry.entryFee)
    setStep('customize')
  }
  
  // ============================================================================
  // CREATE CONTEST
  // ============================================================================
  
  const createContest = async () => {
    if (!selectedOption) return
    
    setStep('creating')
    setIsLoading(true)
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/fantasy/quick-build/create`, {
        option: selectedOption,
        edits: {
          name: editName !== selectedOption.name ? editName : undefined,
          tagline: editTagline !== selectedOption.tagline ? editTagline : undefined,
          entryFee: editEntryFee !== selectedOption.entry.entryFee ? editEntryFee : undefined,
        }
      })
      
      if (response.data.success) {
        toast.success('Contest created!')
        // Navigate to the contest page
        setTimeout(() => {
          navigate(`/fantasy/${response.data.contestId}`)
        }, 1000)
      } else {
        toast.error(response.data.error || 'Failed to create contest')
        setStep('customize')
      }
    } catch (error: any) {
      console.error('Create contest error:', error)
      toast.error(error.response?.data?.error || 'Failed to create contest')
      setStep('customize')
    } finally {
      setIsLoading(false)
    }
  }
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className="min-h-screen py-12 relative overflow-hidden">
      {/* Background decorative blobs */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-40 right-20 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => step === 'style' ? navigate('/') : setStep('style')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 'style' ? 'Home' : 'Start Over'}
          </button>
          <button
            onClick={() => navigate('/fantasy/custom')}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
          >
            <Settings className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400">Custom Builder</span>
          </button>
        </div>
        
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-10 h-10 text-violet-400" />
            <h1 className="text-5xl md:text-6xl font-black text-white">
              Quick <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-500">Build</span>
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Describe your league, AI creates 3 options. Pick one and go! ⚡
          </p>
        </motion.div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['style', 'describe', 'options', 'customize'].map((s, idx) => (
            <div key={s} className="flex items-center">
              <div className={`w-3 h-3 rounded-full transition-all ${
                step === s ? 'bg-violet-500 scale-125' :
                ['style', 'describe', 'options', 'customize'].indexOf(step) > idx ? 'bg-violet-400' :
                'bg-slate-700'
              }`} />
              {idx < 3 && <div className={`w-12 h-0.5 mx-1 ${
                ['style', 'describe', 'options', 'customize'].indexOf(step) > idx ? 'bg-violet-400' : 'bg-slate-700'
              }`} />}
            </div>
          ))}
        </div>

        {/* ============================================ */}
        {/* STEP 1: PICK STYLE */}
        {/* ============================================ */}
        <AnimatePresence mode="wait">
          {step === 'style' && (
            <motion.div
              key="style"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-2xl font-bold text-white text-center mb-8">
                What kind of contest?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* DFS - Pick Players */}
                <button
                  onClick={() => { setContestStyle('dfs'); setStep('describe'); }}
                  className={`p-8 rounded-2xl border-2 transition-all text-left group hover:scale-[1.02] ${
                    contestStyle === 'dfs'
                      ? 'bg-violet-500/20 border-violet-500'
                      : 'bg-slate-800/50 border-slate-700 hover:border-violet-500/50'
                  }`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center text-3xl">
                      👥
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Pick Players</h3>
                      <p className="text-violet-400 text-sm">DFS / Roster Style</p>
                    </div>
                  </div>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Build a roster of NFL players</li>
                    <li>• Salary cap to manage</li>
                    <li>• Players earn fantasy points</li>
                    <li>• Like DraftKings/FanDuel</li>
                  </ul>
                </button>
                
                {/* Survivor - Pick Teams */}
                <button
                  onClick={() => { setContestStyle('survivor'); setStep('describe'); }}
                  className={`p-8 rounded-2xl border-2 transition-all text-left group hover:scale-[1.02] ${
                    contestStyle === 'survivor'
                      ? 'bg-amber-500/20 border-amber-500'
                      : 'bg-slate-800/50 border-slate-700 hover:border-amber-500/50'
                  }`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center text-3xl">
                      🏆
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Pick Teams</h3>
                      <p className="text-amber-400 text-sm">Survivor / Pick'em Style</p>
                    </div>
                  </div>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Pick which teams win</li>
                    <li>• Survivor elimination format</li>
                    <li>• No rosters or salary caps</li>
                    <li>• Simple and fun!</li>
                  </ul>
                </button>
              </div>
            </motion.div>
          )}

          {/* ============================================ */}
          {/* STEP 2: DESCRIBE YOUR LEAGUE */}
          {/* ============================================ */}
          {step === 'describe' && (
            <motion.div
              key="describe"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-3xl mx-auto"
            >
              <div className={`p-8 rounded-2xl border-2 ${
                contestStyle === 'dfs' ? 'border-violet-500/30 bg-violet-500/5' : 'border-amber-500/30 bg-amber-500/5'
              }`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-3xl">{contestStyle === 'dfs' ? '👥' : '🏆'}</span>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Tell us about your league
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Who's playing? What's the vibe? Any theme?
                    </p>
                  </div>
                </div>
                
                <textarea
                  ref={inputRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && description.trim()) {
                      e.preventDefault()
                      generateOptions()
                    }
                  }}
                  placeholder="e.g., 'Delta Delta Delta sorority fantasy party - we want something fun and competitive for our sisters!'"
                  rows={4}
                  className={`w-full px-4 py-3 bg-slate-900/50 border rounded-xl text-white placeholder-gray-500 focus:ring-2 transition-all resize-none ${
                    contestStyle === 'dfs' 
                      ? 'border-violet-500/30 focus:border-violet-500 focus:ring-violet-500/20'
                      : 'border-amber-500/30 focus:border-amber-500 focus:ring-amber-500/20'
                  }`}
                />
                
                {/* Example prompts */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500">Try:</span>
                  {[
                    'Office party for my coworkers',
                    'Family football Sunday',
                    'Greek life competition',
                    'Sports bar regulars',
                    'Friend group for bragging rights'
                  ].map((example) => (
                    <button
                      key={example}
                      onClick={() => setDescription(example)}
                      className={`px-2 py-1 text-xs rounded-md border transition-all ${
                        contestStyle === 'dfs'
                          ? 'border-violet-500/20 hover:border-violet-500/50 text-violet-300 hover:bg-violet-500/10'
                          : 'border-amber-500/20 hover:border-amber-500/50 text-amber-300 hover:bg-amber-500/10'
                      }`}
                    >
                      {example}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => generateOptions()}
                  disabled={!description.trim() || isLoading}
                  className={`mt-6 w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                    contestStyle === 'dfs'
                      ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white disabled:opacity-50'
                      : 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white disabled:opacity-50'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      AI is creating options...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate 3 Options
                    </>
                  )}
                </button>
              </div>
              
              <button
                onClick={() => { setStep('style'); setContestStyle(null); }}
                className="mt-4 text-gray-500 hover:text-gray-300 text-sm mx-auto block"
              >
                ← Change contest style
              </button>
            </motion.div>
          )}

          {/* ============================================ */}
          {/* STEP 3: PICK FROM OPTIONS */}
          {/* ============================================ */}
          {step === 'options' && (
            <motion.div
              key="options"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  Pick your favorite
                </h2>
                <div className="flex items-center gap-3">
                  {canRespin ? (
                    <button
                      onClick={handleRespin}
                      disabled={isLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-gray-300 transition-all disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                      Respin
                    </button>
                  ) : (
                    <span className="text-sm text-gray-500">No more respins</span>
                  )}
                </div>
              </div>
              
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Loader className="w-12 h-12 animate-spin text-violet-400 mx-auto mb-4" />
                    <p className="text-gray-400">AI is generating new options...</p>
                  </div>
                </div>
              ) : (
                <div className="grid gap-6">
                  {options.map((option) => (
                    <OptionCard
                      key={option.id}
                      option={option}
                      isExpanded={expandedOption === option.id}
                      onToggleExpand={() => setExpandedOption(expandedOption === option.id ? null : option.id)}
                      onSelect={() => selectOption(option)}
                      contestStyle={contestStyle}
                    />
                  ))}
                </div>
              )}
              
              {/* Fallback options */}
              <div className="mt-8 flex items-center justify-center gap-4">
                <button
                  onClick={() => { setStep('describe'); }}
                  className="text-gray-500 hover:text-gray-300 text-sm"
                >
                  ← Edit description
                </button>
                <span className="text-gray-700">|</span>
                <button
                  onClick={() => navigate('/fantasy/custom')}
                  className="text-violet-400 hover:text-violet-300 text-sm flex items-center gap-1"
                >
                  <Settings className="w-3 h-3" />
                  Try Custom Builder
                </button>
                <span className="text-gray-700">|</span>
                <button
                  onClick={() => { setStep('style'); setContestStyle(null); setDescription(''); setOptions([]); }}
                  className="text-gray-500 hover:text-gray-300 text-sm"
                >
                  Start over
                </button>
              </div>
            </motion.div>
          )}

          {/* ============================================ */}
          {/* STEP 4: CUSTOMIZE & CREATE */}
          {/* ============================================ */}
          {(step === 'customize' || step === 'creating') && selectedOption && (
            <motion.div
              key="customize"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-3xl mx-auto"
            >
              <div className={`p-8 rounded-2xl border-2 ${
                contestStyle === 'dfs' ? 'border-violet-500/30 bg-slate-800/80' : 'border-amber-500/30 bg-slate-800/80'
              }`}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <CheckCircle className={contestStyle === 'dfs' ? 'text-violet-400' : 'text-amber-400'} />
                    Almost done!
                  </h2>
                  <button
                    onClick={() => setStep('options')}
                    className="text-gray-500 hover:text-gray-300 text-sm"
                  >
                    ← Back to options
                  </button>
                </div>
                
                {/* Editable fields */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Contest Name</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Tagline</label>
                    <input
                      type="text"
                      value={editTagline}
                      onChange={(e) => setEditTagline(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Entry Fee</label>
                    <select
                      value={editEntryFee}
                      onChange={(e) => setEditEntryFee(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                    >
                      <option value={0}>Free</option>
                      <option value={1}>$1</option>
                      <option value={3}>$3</option>
                      <option value={5}>$5</option>
                      <option value={10}>$10</option>
                      <option value={20}>$20</option>
                      <option value={25}>$25</option>
                      <option value={50}>$50</option>
                    </select>
                  </div>
                </div>
                
                {/* Summary */}
                <div className="p-4 bg-slate-900/50 rounded-xl mb-6 space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <span className="text-gray-500">Format:</span>
                      <span className="text-gray-300 ml-2">{selectedOption.gameSelection.description}</span>
                    </div>
                  </div>
                  {selectedOption.rosterFormat && (
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-gray-500 mt-0.5" />
                      <div>
                        <span className="text-gray-500">Roster:</span>
                        <span className="text-gray-300 ml-2">{selectedOption.rosterFormat.description}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <span className="text-gray-500">Scoring:</span>
                      <span className="text-gray-300 ml-2">{selectedOption.scoring.description}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Trophy className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <span className="text-gray-500">Prizes:</span>
                      <span className="text-gray-300 ml-2">{selectedOption.entry.prizeDescription}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <span className="text-gray-500">Lock:</span>
                      <span className="text-gray-300 ml-2">{selectedOption.timing.lockDescription}</span>
                    </div>
                  </div>
                </div>
                
                {/* Creative hook */}
                <div className={`p-4 rounded-xl mb-6 ${
                  contestStyle === 'dfs' ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-amber-500/10 border border-amber-500/20'
                }`}>
                  <p className={`text-sm ${contestStyle === 'dfs' ? 'text-violet-300' : 'text-amber-300'}`}>
                    ✨ {selectedOption.creativeHook}
                  </p>
                </div>
                
                {/* Create button */}
                <button
                  onClick={createContest}
                  disabled={isLoading || step === 'creating'}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                    contestStyle === 'dfs'
                      ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white disabled:opacity-50'
                      : 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white disabled:opacity-50'
                  }`}
                >
                  {step === 'creating' ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Creating your contest...
                    </>
                  ) : (
                    <>
                      <Trophy className="w-5 h-5" />
                      Create Contest
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

// ============================================================================
// OPTION CARD COMPONENT
// ============================================================================

function OptionCard({ 
  option, 
  isExpanded, 
  onToggleExpand, 
  onSelect,
  contestStyle
}: { 
  option: ContestOption
  isExpanded: boolean
  onToggleExpand: () => void
  onSelect: () => void
  contestStyle: 'dfs' | 'survivor' | null
}) {
  const colorClass = contestStyle === 'dfs' ? 'violet' : 'amber'
  
  return (
    <motion.div
      layout
      className={`rounded-2xl border-2 overflow-hidden transition-all ${
        isExpanded 
          ? `border-${colorClass}-500 bg-slate-800` 
          : `border-slate-700 bg-slate-800/50 hover:border-${colorClass}-500/50`
      }`}
    >
      {/* Header - always visible */}
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-2xl font-black ${
                contestStyle === 'dfs' ? 'text-violet-400' : 'text-amber-400'
              }`}>
                {option.id}
              </span>
              <h3 className="text-xl font-bold text-white">{option.name}</h3>
            </div>
            <p className="text-gray-400">{option.tagline}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleExpand}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              {isExpanded ? <ChevronUp /> : <ChevronDown />}
            </button>
            <button
              onClick={onSelect}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                contestStyle === 'dfs'
                  ? 'bg-violet-600 hover:bg-violet-500 text-white'
                  : 'bg-amber-600 hover:bg-amber-500 text-white'
              }`}
            >
              Select
            </button>
          </div>
        </div>
        
        {/* Quick info row */}
        <div className="flex flex-wrap gap-3 mt-4">
          <span className={`px-2 py-1 rounded-md text-xs font-medium ${
            contestStyle === 'dfs' ? 'bg-violet-500/20 text-violet-300' : 'bg-amber-500/20 text-amber-300'
          }`}>
            {option.gameSelection.description}
          </span>
          <span className="px-2 py-1 bg-slate-700 rounded-md text-xs text-gray-300">
            {option.entry.feeDescription}
          </span>
          <span className="px-2 py-1 bg-slate-700 rounded-md text-xs text-gray-300">
            {option.scoring.type === 'preset' ? option.scoring.preset : 'Custom Scoring'}
          </span>
          <span className={`px-2 py-1 rounded-md text-xs ${
            option.vibe === 'casual' ? 'bg-green-500/20 text-green-300' :
            option.vibe === 'competitive' ? 'bg-red-500/20 text-red-300' :
            'bg-purple-500/20 text-purple-300'
          }`}>
            {option.vibe}
          </span>
        </div>
      </div>
      
      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-700"
          >
            <div className="p-6 space-y-4 bg-slate-900/50">
              {/* Creative hook */}
              <div className={`p-3 rounded-lg ${
                contestStyle === 'dfs' ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-amber-500/10 border border-amber-500/20'
              }`}>
                <p className={`text-sm ${contestStyle === 'dfs' ? 'text-violet-300' : 'text-amber-300'}`}>
                  ✨ {option.creativeHook}
                </p>
              </div>
              
              {/* Details grid */}
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {/* Game Selection */}
                <div>
                  <h4 className="font-semibold text-gray-300 mb-1 flex items-center gap-1">
                    <Target className="w-3 h-3" /> Games
                  </h4>
                  <p className="text-gray-400">{option.gameSelection.description}</p>
                  {option.gameSelection.games && option.gameSelection.games.length > 0 && (
                    <ul className="mt-1 text-gray-500 text-xs">
                      {option.gameSelection.games.map((g, i) => (
                        <li key={i}>• {g}</li>
                      ))}
                    </ul>
                  )}
                </div>
                
                {/* Roster (DFS only) */}
                {option.rosterFormat && (
                  <div>
                    <h4 className="font-semibold text-gray-300 mb-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Roster
                    </h4>
                    <p className="text-gray-400">{option.rosterFormat.description}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {option.rosterFormat.totalPlayers} players • ${option.rosterFormat.salaryCap.toLocaleString()} cap
                    </p>
                  </div>
                )}
                
                {/* Scoring */}
                <div>
                  <h4 className="font-semibold text-gray-300 mb-1 flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Scoring
                  </h4>
                  <p className="text-gray-400">{option.scoring.description}</p>
                  {option.scoring.example && (
                    <p className="text-gray-500 text-xs mt-1 italic">{option.scoring.example}</p>
                  )}
                </div>
                
                {/* Team Scoring (Survivor only) */}
                {option.teamScoring && (
                  <div>
                    <h4 className="font-semibold text-gray-300 mb-1 flex items-center gap-1">
                      <Trophy className="w-3 h-3" /> Win Condition
                    </h4>
                    <p className="text-gray-400">{option.teamScoring.description}</p>
                  </div>
                )}
                
                {/* Entry & Prizes */}
                <div>
                  <h4 className="font-semibold text-gray-300 mb-1 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Entry & Prizes
                  </h4>
                  <p className="text-gray-400">{option.entry.feeDescription}</p>
                  <p className="text-gray-500 text-xs mt-1">{option.entry.prizeDescription}</p>
                </div>
                
                {/* Timing */}
                <div>
                  <h4 className="font-semibold text-gray-300 mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Lock Timing
                  </h4>
                  <p className="text-gray-400">{option.timing.lockDescription}</p>
                  {option.timing.rollingLock && (
                    <p className="text-gray-500 text-xs mt-1">✏️ Can swap between games!</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

